<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { Carousel, Navigation, Slide } from 'vue3-carousel'

import type { Impact, ImpactColor, ImpactType, IProject } from '@/api/interfaces'
import type { ICarousel } from '@/helpers/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'

import PublicProjectCard from '@/components/public-page/PublicProjectCard.vue'
import { resolveParentScaleContext, sumParentScaleEquivalentAmount } from '@/helpers/project'

const PublicMapboxCanvas = defineAsyncComponent(
  () => import('@/components/public-page/PublicMapboxCanvas.vue'),
)

interface Props {
  projects: IProject[]
  colorSettings: Record<ImpactType, ImpactColor>
}

const props = defineProps<Props>()

const editorStore = useEditorStore()
const publicDataStore = usePublicData()

const mapSection = ref<HTMLElement | null>(null)
const carousel = ref<ICarousel | null>(null)
const currentSlide = ref(0)
const shouldLoadMap = ref(false)
const focusRequestKey = ref(0)
let observer: IntersectionObserver | null = null

const accountSignupDate = computed(() => new Date(publicDataStore.signupDate).getTime())
const mapboxToken = computed(() => import.meta.env.VITE_MAPBOX_KEY)
const hasMapboxToken = computed(() => Boolean(mapboxToken.value))

const projectsToDisplay = computed(() =>
  props.projects
    .filter(({ type }) =>
      (Object.keys(props.colorSettings) as Array<ImpactType>).some(
        (colorSettingImpactType) => type === colorSettingImpactType,
      ),
    )
    .filter(({ type }) => editorStore.editor.icon.impactDisplay[type])
    .filter(({ projectId, parentProjectId, type, status, statusInitiatedAt, geoJSON }) => {
      const hasProjectGeometry = Boolean(geoJSON?.features?.length)

      if (!hasProjectGeometry) {
        return false
      }

      return publicDataStore.impacts?.length
        ? publicDataStore.impacts.some(
            (impact) =>
              resolveParentScaleContext(impact, publicDataStore.projectList).familyRootProjectId ===
                projectId ||
              (impact.type === 'carbon' &&
                impact.amount > 0 &&
                type === 'carbon' &&
                (status === 'deprecated'
                  ? accountSignupDate.value < new Date(statusInitiatedAt).getTime()
                  : true)),
          )
        : status === 'approved' && !parentProjectId
    }),
)

const isTwoItemCarousel = computed(() => projectsToDisplay.value.length === 2)

const impactForListedProject = (project: IProject): Impact | undefined => {
  const impacts = publicDataStore.impacts ?? []
  const amount = sumParentScaleEquivalentAmount(
    project.type,
    impacts,
    publicDataStore.projectList,
    {
      familyRootProjectId: project.projectId,
    },
  )
  if (amount > 0) return { type: project.type, amount }

  return impacts.find(
    (impact) => impact.type === 'carbon' && impact.amount > 0 && project.type === 'carbon',
  )
}

const breakPoints = computed(() => ({
  0: { itemsToShow: 1, snapAlign: 'center' as const },
  960: { itemsToShow: 2, snapAlign: 'center' as const },
  1260: { itemsToShow: isTwoItemCarousel.value ? 2 : 3, snapAlign: 'center' as const },
}))

const syncCarouselToSelectedProject = async (projectId: string | null) => {
  if (!projectId) return

  const selectedIndex = projectsToDisplay.value.findIndex(
    (project) => project.projectId === projectId,
  )
  if (selectedIndex < 0) return

  currentSlide.value = selectedIndex
  await nextTick()
  carousel.value?.slideTo(selectedIndex)
}

const requestProjectFocus = () => {
  focusRequestKey.value = Date.now()
}

const handleProjectSelection = async (projectId: string, shouldFocus = true) => {
  await publicDataStore.selectProject(projectId)

  if (shouldFocus) {
    await nextTick()
    requestProjectFocus()
  }
}

const setupIntersectionObserver = () => {
  if (!mapSection.value || shouldLoadMap.value) {
    return
  }

  if (!('IntersectionObserver' in window)) {
    shouldLoadMap.value = true
    return
  }

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries

      if (!entry?.isIntersecting) {
        return
      }

      shouldLoadMap.value = true
      observer?.disconnect()
      observer = null
    },
    { rootMargin: '300px 0px' },
  )

  observer.observe(mapSection.value)
}

watch(
  projectsToDisplay,
  () => {
    if (!projectsToDisplay.value.length) {
      publicDataStore.setSelectedProjectId(null)
      currentSlide.value = 0
      return
    }

    const selectedProjectExists = projectsToDisplay.value.some(
      (project) => project.projectId === publicDataStore.selectedProjectId,
    )

    if (!selectedProjectExists) {
      publicDataStore.setSelectedProjectId(projectsToDisplay.value[0].projectId)
    }

    void syncCarouselToSelectedProject(publicDataStore.selectedProjectId)
  },
  { deep: true, immediate: true },
)

watch(
  () => publicDataStore.selectedProjectId,
  (projectId) => {
    void syncCarouselToSelectedProject(projectId)
  },
  { immediate: true },
)

watch(currentSlide, (slideIndex) => {
  const project = projectsToDisplay.value[slideIndex]

  if (!project || project.projectId === publicDataStore.selectedProjectId) {
    return
  }

  void handleProjectSelection(project.projectId, true)
})

onMounted(() => {
  setupIntersectionObserver()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<template>
  <section v-if="projectsToDisplay.length" ref="mapSection" class="public-map-section">
    <v-container>
      <div class="map-container">
        <div class="map-stage">
          <div
            v-if="!hasMapboxToken || !shouldLoadMap"
            aria-hidden="true"
            class="map-stage-placeholder map-stage-placeholder--loading"
          >
            <v-skeleton-loader class="map-stage-skeleton" type="image" />
          </div>
          <PublicMapboxCanvas
            v-else
            :color-settings="colorSettings"
            :focus-request-key="focusRequestKey"
            :projects="projectsToDisplay"
            :selected-project-evidence="publicDataStore.getSelectedProjectEvidence"
            :selected-project-id="publicDataStore.selectedProjectId"
            :selected-project-planting-geo-json="publicDataStore.getSelectedProjectPlantingGeoJSON"
            :selected-project-plastic-collection-geo-json="
              publicDataStore.getSelectedProjectPlasticCollectionGeoJSON
            "
            @select-project="(projectId) => handleProjectSelection(projectId)"
          />

          <div class="map-carousel-overlay">
            <Carousel
              id="public-map-carousel"
              ref="carousel"
              v-model="currentSlide"
              :breakpoints="breakPoints"
              :class="{ 'map-carousel--two-items': isTwoItemCarousel }"
              :items-to-show="1"
              :snap-align="'center'"
              :transition="350"
              :wrap-around="false"
            >
              <Slide v-for="project in projectsToDisplay" :key="project.projectId">
                <PublicProjectCard
                  :active="publicDataStore.selectedProjectId === project.projectId"
                  :color-settings="colorSettings"
                  :impact="impactForListedProject(project)"
                  :project="project"
                  compact
                  interactive
                  @select="() => handleProjectSelection(project.projectId, true)"
                />
              </Slide>
              <template #addons>
                <Navigation v-if="projectsToDisplay.length > 1" />
              </template>
            </Carousel>
          </div>
        </div>
      </div>
    </v-container>
  </section>
</template>

<style lang="scss">
.public-map-section {
  margin-bottom: 20px;

  @include media('md-and-up') {
    margin-bottom: 80px;
  }
}

.map-container {
  width: 100%;
}

.map-stage {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  height: 500px;
  background: #07111f;

  @include media('sm-and-up') {
    height: 560px;
  }

  @include media('md-and-up') {
    height: 700px;
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto 0 0;
    height: 170px;
    background: linear-gradient(180deg, rgb(7 17 31 / 0%) 0%, rgb(7 17 31 / 68%) 100%);
    pointer-events: none;
    z-index: 2;
  }
}

.map-stage-placeholder {
  width: 100%;
  display: block;
}

.map-stage-placeholder--loading {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.map-stage-skeleton {
  width: 100%;
  height: 100%;
}

.map-stage-skeleton :deep(.v-skeleton-loader__image) {
  width: 100%;
  height: 100%;
  border-radius: 0;
}

.map-carousel-overlay {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: 3;
  padding: 0 6px 10px;
  display: flex;
  justify-content: center;

  @include media('sm-and-up') {
    padding: 0 28px 5px;
  }

  @include media('md-and-up') {
    padding: 0 75px 15px;
  }
}

.map-carousel-overlay .carousel {
  position: relative;
  width: 100%;
  max-width: 950px;
}

.map-carousel-overlay .carousel__viewport {
  padding: 10px 0;
}

.map-carousel-overlay .carousel__track {
  align-items: stretch;
}

.map-carousel-overlay .carousel__slide {
  padding: 0;
  transition:
    transform 150ms ease-in-out,
    opacity 150ms ease-in-out;
  opacity: 0.92;
  display: flex;
  justify-content: center;

  @include media('md-and-up') {
    &--active {
      opacity: 1;
    }
  }
}

.map-carousel-overlay .carousel__slide > * {
  width: calc(100% - 8px);
  max-width: 296px;

  @include media('sm-and-up') {
    width: 296px;
  }

  @include media('md-and-up') {
    width: 280px;
    max-width: 280px;
  }
}

.map-carousel-overlay .carousel.map-carousel--two-items {
  @include media('md-and-up') {
    .carousel__track {
      justify-content: center;
    }

    .carousel__slide:first-child {
      justify-content: flex-end;
    }

    .carousel__slide:last-child {
      justify-content: flex-start;
    }

    .carousel__slide:first-child > * {
      margin-right: 12px;
    }

    .carousel__slide:last-child > * {
      margin-left: 12px;
    }
  }
}

.map-carousel-overlay .carousel__prev,
.map-carousel-overlay .carousel__next {
  box-sizing: content-box;
  z-index: 4;
  width: 22px;
  height: 22px;
  color: var(--ui-white);
  background: transparent;
  top: 50%;
  transform: translateY(calc(-50% - 4px));

  @include media('sm-and-up') {
    width: 32px;
    height: 32px;
  }
}

.map-carousel-overlay .carousel__prev {
  display: none;

  @include media('md-and-up') {
    display: block;
    left: -54px;
  }
}

.map-carousel-overlay .carousel__next {
  display: none;

  @include media('md-and-up') {
    display: block;
    right: -54px;
  }
}

.map-carousel-overlay .carousel__icon {
  width: 100%;
  height: 100%;
}
</style>
